import { Modal, ModalOverlay, ModalContent } from "@chakra-ui/react";
import { translate } from "ui/i18n";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { useWindowWidth } from "ui/utils/hooks";
import { CloseButton } from "ui/pages/app/components/buttons/CloseButton";
import { INInput } from "ui/pages/app/components/inputs/INInput";
import { useForm } from "react-hook-form";
import { Inputs } from "ui/pages/inputsType";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { selectIsFoldersLoading } from "domain/folders/folders.selectors";
import {
  addSeanceToFolder,
  createFolder,
  updateFolder,
} from "domain/folders/folders.actions";
import { Snackbar } from "ui/pages/snackbar";
import { Folder } from "domain/entities/folders.entities";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  setLastCreatedFolder?: (folderId: number | null) => void;
  folder?: Folder;
  setFolder?: (folder: Folder | null) => void;
  seanceId?: number;
}

export function ModalManageFolder({
  isOpen,
  onClose,
  setLastCreatedFolder,
  folder,
  setFolder,
  seanceId,
}: IProps) {
  const isDesktop = useWindowWidth();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectIsFoldersLoading);
  const { register, handleSubmit, formState, reset } = useForm<Inputs>({
    mode: "onSubmit",
    defaultValues: {
      label: folder?.label || "",
    },
  });
  const { errors, isDirty } = formState;

  const handleFormSubmit = async (data: Inputs) => {
    if (folder?.id) {
      const result = await dispatch(
        updateFolder({ label: data.label, id: folder.id })
      ).unwrap();
      if (result.success) {
        Snackbar.showSuccess({
          title: translate("ns1:Folders.FolderUpdated"),
        });
        setFolder({ ...folder, label: data.label });
      }
    } else {
      const result = await dispatch(
        createFolder({ label: data.label })
      ).unwrap();
      if (result.success) {
        Snackbar.showSuccess({
          title: translate("ns1:Folders.FolderCreated"),
        });
        setLastCreatedFolder(result.folder.id);
        if (seanceId) {
          await dispatch(
            addSeanceToFolder({ folderId: result.folder.id, seanceId })
          ).unwrap();
        }
      }
      reset();
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW="750px" margin="20px" className="!rounded-[30px] p-8">
        <CloseButton
          onClick={onClose}
          alternativeText={translate("ns1:Button.Cancel")}
        />
        <div className="mt-12 flex flex-col items-center">
          <h4 className="w-fit text-center font-bold  text-secondaryGrey-900">
            {translate(
              `ns1:Folders.${folder?.id ? "RenameTheFolder" : "NameTheFolder"}`
            )}
          </h4>
        </div>

        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="mt-6 pb-2 md:mt-12 md:px-20"
        >
          <INInput
            error={errors.label}
            label={translate("ns1:Folders.FolderName") + "*"}
            placeholder={translate("ns1:Folders.FolderNamePlaceholder")}
            register={{
              ...register("label", {
                required: translate("ns1:Input.Errors.Required"),
              }),
            }}
            className="mb-2 md:mb-4"
          />
          <PrimaryButton
            type="submit"
            height={isDesktop ? "52px" : "46px"}
            width="100%"
            className="mt-6 md:mt-12"
            disabled={!isDirty || isLoading}
          >
            {translate("ns1:Button.Validate")}
          </PrimaryButton>
        </form>
      </ModalContent>
    </Modal>
  );
}
